<div class="w-full mx-auto p-5 space-y-6">
  <div class="flex justify-between items-center">
    <div>
      <h1 class="text-2xl font-bold text-gray-800">Detalle de Devolución</h1>
      <p class="text-gray-500 mt-1">Información completa del comprobante de devolución</p>
    </div>
    <a routerLink="/refund-vouchers" class="text-blue-600 hover:text-blue-800 flex items-center gap-2">
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"/>
      </svg>
      Volver a Devoluciones
    </a>
  </div>

  <!-- Loading state -->
  <div *ngIf="!refundVoucher" class="flex justify-center items-center p-10">
    <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
  </div>

  <!-- Main content when loaded -->
  <div *ngIf="refundVoucher" class="space-y-6">
    <!-- Header card with status -->
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 class="text-xl font-semibold text-gray-800">{{ refundVoucher.documentNumber }}</h2>
          <p class="text-gray-500">{{ refundVoucher.documentDate | date:'fullDate' }}</p>
        </div>
        
        <div class="flex flex-wrap gap-3 items-center">
          <!-- Status badge -->
          <span class="px-4 py-2 rounded-full text-sm font-semibold border-2"
                [ngClass]="getStatusClass(refundVoucher.status)">
            <span *ngIf="refundVoucher.status === 'Completed'" class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              {{ refundVoucher.statusDescription }}
            </span>
            <span *ngIf="refundVoucher.status !== 'Completed'">
              {{ refundVoucher.statusDescription }}
            </span>
          </span>
          
          <!-- Botón de descarga del PDF (siempre disponible) -->
          <div class="flex flex-wrap gap-2">
            <button (click)="downloadPdf()"
                    [disabled]="isDownloadingPdf"
                    class="px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-800 transition flex items-center gap-2 shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
              </svg>
              <span>{{ isDownloadingPdf ? 'Generando...' : 'Descargar PDF' }}</span>
            </button>
          </div>

          <!-- Action buttons - Solo se muestran si NO está completado o cancelado -->
          <div *ngIf="shouldShowActionButtons()" class="flex flex-wrap gap-2">
            <button *ngIf="refundVoucher.availableActions.includes('approve')" 
                    (click)="openApprovalModal()"
                    class="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition flex items-center gap-2 shadow-sm hover:shadow-md">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Aprobar
            </button>
            
            <button *ngIf="refundVoucher.availableActions.includes('process')" 
                    (click)="openProcessModal()"
                    class="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition flex items-center gap-2 shadow-sm hover:shadow-md">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Procesar
            </button>
            
            <!-- 🆕 Botón Anular: oculto si ya está Completed, Cancelled o Rejected (shouldShowActionButtons) -->
            <button *ngIf="refundVoucher.availableActions.includes('cancel')"
                    (click)="openCancelModal()"
                    class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition flex items-center gap-2 shadow-sm hover:shadow-md">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
              Anular
            </button>
          </div>

          <!-- Mensaje informativo cuando está completado -->
          <div *ngIf="refundVoucher.status === 'Completed'" class="flex items-center gap-2 text-sm text-gray-600 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
            <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span class="font-medium">Devolución completada exitosamente</span>
          </div>
        </div>
      </div>
      
      <!-- Progress bar for processing status -->
      <div *ngIf="refundVoucher.status === 'Processing' || refundVoucher.status === 'Completed'" class="mt-4">
        <div class="flex justify-between items-center text-sm mb-2">
          <span class="font-medium" [ngClass]="refundVoucher.status === 'Completed' ? 'text-green-700' : 'text-gray-700'">
            Progreso: {{ refundVoucher.processingPercentage }}
          </span>
          <span class="font-semibold" [ngClass]="refundVoucher.status === 'Completed' ? 'text-green-700' : 'text-gray-700'">
            {{ formatCurrencyWithCode(refundVoucher.processedAmount || 0) }} / {{ formatCurrencyWithCode(refundVoucher.refundAmount) }}
          </span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div class="h-3 rounded-full transition-all duration-500" 
               [ngClass]="refundVoucher.status === 'Completed' ? 'bg-green-600' : 'bg-purple-600'"
               [style.width]="refundVoucher.processingPercentage"></div>
        </div>
        <div *ngIf="refundVoucher.status === 'Completed' && refundVoucher.processedDate" class="mt-2 text-xs text-gray-500 flex items-center gap-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          Procesado el {{ refundVoucher.processedDate | date:'medium' }} por {{ refundVoucher.processedBy || 'Usuario' }}
        </div>
        <!-- 🆕 Fecha efectiva de pago bancario -->
        <div *ngIf="refundVoucher.paymentDate" class="mt-1 text-xs text-gray-500 flex items-center gap-1">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.25 8.25h19.5M2.25 12h19.5m-16.5 5.25h9"/>
          </svg>
          Fecha de pago: {{ refundVoucher.paymentDate | date:'mediumDate' }}
        </div>
      </div>

      <!-- 🆕 Aviso cuando la devolución está anulada -->
      <div *ngIf="refundVoucher.status === 'Cancelled' && refundVoucher.cancelledAt"
           class="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
        <div class="font-semibold">Devolución anulada</div>
        <div class="text-xs text-red-700 mt-1">
          {{ refundVoucher.cancelledAt | date:'medium' }} por {{ refundVoucher.cancelledBy || 'Sistema' }}
        </div>
        <div *ngIf="refundVoucher.cancellationReason" class="text-xs text-red-700 mt-1">
          Motivo: {{ refundVoucher.cancellationReason }}
        </div>
      </div>
    </div>
    
    <!-- Main details -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Left column: Basic details -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-medium text-gray-800 mb-4">Información General</h3>
        
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">Tipo de Devolución</p>
              <p class="font-medium">{{ refundVoucher.refundTypeDescription }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500">Concepto</p>
              <p class="font-medium">{{ refundVoucher.refundConceptDescription }}</p>
            </div>
          </div>
          
          <div>
            <p class="text-sm text-gray-500">Proyecto</p>
            <p class="font-medium">{{ refundVoucher.project?.projectId }} - {{ refundVoucher.project?.name }}</p>
          </div>
          
          <div>
            <p class="text-sm text-gray-500">Participante</p>
            <div class="flex items-center gap-2">
              <p class="font-medium"> {{ refundVoucher.participant?.typeDocument }} {{ refundVoucher.participant?.documentId }} - {{ refundVoucher.participant?.firstName }} {{ refundVoucher.participant?.lastName }}</p>
              <a *ngIf="refundVoucher.participant?.id" 
                 [routerLink]="['/participants/profile', refundVoucher.participant.id]"
                 class="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                 title="Ver perfil del participante">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
                Ver Perfil
              </a>
            </div>
          </div>
          
          <div *ngIf="refundVoucher.enrollment">
            <p class="text-sm text-gray-500">Inscripción</p>
            <div class="flex items-center gap-2">
              <p class="font-medium">#{{ refundVoucher.enrollment?.id }}</p>
              <a [routerLink]="['/enrollments/view', refundVoucher.enrollment.id]"
                 class="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                 title="Ver detalles de la inscripción">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
                Ver Detalles
              </a>
            </div>
          </div>
          
          <div *ngIf="refundVoucher.paymentPlan">
            <p class="text-sm text-gray-500">Plan de Pago</p>
            <p class="font-medium">Cuota #{{ refundVoucher.paymentPlan?.installmentNumber }}</p>
          </div>
          
          <div *ngIf="refundVoucher.excessPayment">
            <p class="text-sm text-gray-500">Excedente</p>
            <p class="font-medium">#{{ refundVoucher.excessPayment?.id }}</p>
          </div>         
          
          
          <!-- Nueva sección: Comprobante de Ingreso Origen para Excedentes -->
          <div *ngIf="refundVoucher.excessPayment?.originIncomeReceipt" 
               class="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div class="flex items-center justify-between mb-3">
              <p class="text-sm font-medium text-blue-800">📄 Comprobante de Ingreso Origen</p>
              <a [routerLink]="['/income-receipts/view', refundVoucher.excessPayment?.originIncomeReceipt?.id]"
                 class="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 bg-white px-2 py-1 rounded border"
                 title="Ver comprobante de ingreso">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                Ver Comprobante
              </a>
            </div>
            
            <!-- Información básica del comprobante -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <p class="text-sm text-blue-700">
                  <strong>ID:</strong> {{ refundVoucher.excessPayment?.originIncomeReceipt?.id }}
                </p>
                <p class="text-sm text-blue-700">
                  <strong>Fecha:</strong> {{ refundVoucher.excessPayment?.originIncomeReceipt?.createdAt | date:'shortDate' }}
                </p>
                <p class="text-sm text-blue-700">
                  <strong>Concepto:</strong> {{ refundVoucher.excessPayment?.originIncomeReceipt?.concept }}
                </p>
                <p class="text-sm text-blue-700">
                  <strong>Tipo:</strong> {{ refundVoucher.excessPayment?.originIncomeReceipt?.receiptType }}
                </p>
              </div>
              <div>
                <p class="text-sm text-blue-700" *ngIf="refundVoucher.excessPayment?.originIncomeReceipt?.observations">
                  <strong>Observaciones:</strong> {{ refundVoucher.excessPayment?.originIncomeReceipt?.observations }}
                </p>
              </div>
            </div>
            
            <!-- Detalle financiero -->
            <div class="bg-white p-3 rounded border border-blue-200">
              <p class="text-sm font-medium text-blue-800 mb-2">💰 Detalle Financiero</p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div class="space-y-2">
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-gray-600">Monto Recibido:</span>
                    <span class="text-sm font-medium text-green-600">
                      {{ refundVoucher.excessPayment?.originIncomeReceipt?.receivedAmount | currency:'COP':'symbol':'1.0-0' }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-gray-600">Monto Bruto:</span>
                    <span class="text-sm font-medium">
                      {{ refundVoucher.excessPayment?.originIncomeReceipt?.grossAmount | currency:'COP':'symbol':'1.0-0' }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-gray-600">Comisiones/Descuentos:</span>
                    <span class="text-sm font-medium text-red-600">
                      -{{ refundVoucher.excessPayment?.originIncomeReceipt?.totalFees | currency:'COP':'symbol':'1.0-0' }}
                    </span>
                  </div>
                  <div class="flex justify-between items-center border-t pt-2">
                    <span class="text-sm font-medium text-gray-700">Monto Neto Aplicado:</span>
                    <span class="text-sm font-medium text-blue-600">
                      {{ refundVoucher.excessPayment?.originIncomeReceipt?.netAmount | currency:'COP':'symbol':'1.0-0' }}
                    </span>
                  </div>
                </div>
                
                <!-- Destacar el excedente -->
                <div class="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <div class="flex items-center gap-2 mb-2">
                    <svg class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                    </svg>
                    <span class="text-sm font-medium text-yellow-800">Excedente Generado</span>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-yellow-700 underline decoration-2 decoration-yellow-500">
                      {{ refundVoucher.excessPayment?.excessAmount | currency:'COP':'symbol':'1.0-0' }}
                    </p>
                    <p class="text-xs text-yellow-600 mt-1">
                      ({{ refundVoucher.excessPayment?.originIncomeReceipt?.receivedAmount | currency:'COP':'symbol':'1.0-0' }} - 
                      {{ refundVoucher.excessPayment?.originIncomeReceipt?.netAmount | currency:'COP':'symbol':'1.0-0' }})
                    </p>
                  </div>
                  <div class="mt-2 text-xs text-yellow-700">
                    <p><strong>Estado del Excedente:</strong> {{ refundVoucher.excessPayment?.statusDescription }}</p>
                    <p><strong>Usado:</strong> {{ refundVoucher.excessPayment?.usedAmount | currency:'COP':'symbol':'1.0-0' }}</p>
                    <p><strong>Disponible:</strong> {{ refundVoucher.excessPayment?.availableAmount | currency:'COP':'symbol':'1.0-0' }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <p class="text-sm text-gray-500">Banco</p>
            <p class="font-medium">{{ refundVoucher.bank?.bankName }}</p>
          </div>
          
          <div *ngIf="refundVoucher.bankTransactionReference">
            <p class="text-sm text-gray-500">Referencia Bancaria</p>
            <p class="font-medium">{{ refundVoucher.bankTransactionReference }}</p>
          </div>
        </div>
      </div>
      
      <!-- Right column: Financial details -->
      <div class="bg-white rounded-xl border border-gray-200 p-6">
        <h3 class="text-lg font-medium text-gray-800 mb-4">Información Financiera</h3>
        
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">Monto Total</p>
              <p class="text-lg font-semibold text-blue-700">{{ formatCurrencyWithCode(refundVoucher.refundAmount) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500">Moneda</p>
              <p class="font-medium">{{ getCurrencyCode() }}</p>
            </div>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">Monto Procesado</p>
              <p class="text-lg font-semibold text-green-700">{{ formatCurrencyWithCode(refundVoucher.processedAmount || 0) }}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500">Monto Pendiente</p>
              <p class="text-lg font-semibold text-amber-600">{{ formatCurrencyWithCode(refundVoucher.pendingAmount || 0) }}</p>
            </div>
          </div>
          
          <div *ngIf="refundVoucher.currency !== 'COP'">
            <p class="text-sm text-gray-500">Tasa de Cambio</p>
            <p class="font-medium">{{ refundVoucher.exchangeRate }}</p>
          </div>
          
          <div class="pt-4 border-t border-gray-100">
            <p class="text-sm text-gray-500">Concepto</p>
            <p class="font-medium">{{ refundVoucher.concept }}</p>
          </div>
          
          <div *ngIf="refundVoucher.justification">
            <p class="text-sm text-gray-500">Justificación</p>
            <p class="font-medium">{{ refundVoucher.justification }}</p>
          </div>
          
          <div *ngIf="refundVoucher.observations">
            <p class="text-sm text-gray-500">Observaciones</p>
            <p class="font-medium">{{ refundVoucher.observations }}</p>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Nueva sección: Detalles de Documentos y Descuentos -->
    <div *ngIf="getRefundVoucherItems().length > 0" class="bg-white rounded-xl border border-gray-200 p-6">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-lg font-medium text-gray-800">
          Cuotas Afectadas por la Devolución
          <span class="ml-2 text-sm font-normal text-gray-500">({{ getRefundVoucherItems().length }} documento(s))</span>
        </h3>
        <button (click)="toggleItemDetails()" 
                class="text-blue-600 hover:text-blue-800 flex items-center gap-2 text-sm font-medium">
          <span>{{ showItemDetails ? 'Ocultar' : 'Ver' }} Detalles</span>
          <svg class="w-4 h-4 transition-transform" 
               [class.rotate-180]="showItemDetails" 
               fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
      </div>
      
      <!-- Nota sobre distribución automática (para devoluciones de plan) -->
      <div *ngIf="getRefundVoucherItems().length > 1" 
           class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div class="flex items-start">
          <i class="pi pi-info-circle text-blue-500 mr-2 mt-0.5"></i>
          <div>
            <p class="text-sm text-blue-800">
              <strong>Distribución Automática:</strong> El monto de esta devolución fue distribuido automáticamente 
              entre los comprobantes de ingreso listados a continuación, empezando por el pago más reciente.
            </p>
          </div>
        </div>
      </div>
      
      <!-- Resumen financiero total -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <h4 class="text-sm font-medium text-blue-800 mb-3">💰 Resumen Financiero Total</h4>
        
        <!-- ✅ MEJORADO: Información específica para excedentes -->
        <div *ngIf="isExcessRefund() && getExcessPaymentInfo()" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <div class="flex items-center justify-between mb-2">
            <p class="text-sm font-medium text-yellow-800">📄 Origen del Excedente</p>
            <a *ngIf="getExcessPaymentInfo()?.originReceipt?.incomeReceiptId" 
               [routerLink]="['/income-receipts/view', getExcessPaymentInfo()?.originReceipt?.incomeReceiptId]"
               class="text-yellow-700 hover:text-yellow-900 text-xs flex items-center gap-1 bg-white px-2 py-1 rounded border"
               title="Ver comprobante de ingreso origen">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
              </svg>
              Ver Comprobante #{{ getExcessPaymentInfo()?.originReceipt?.incomeReceiptId }}
            </a>
          </div>
          <div class="text-xs text-yellow-700">
            <p><strong>Concepto:</strong> {{ getExcessPaymentInfo()?.originReceipt?.concept }}</p>
            <p><strong>Fecha:</strong> {{ getExcessPaymentInfo()?.originReceipt?.transactionDate | date:'shortDate' }}</p>
          </div>
        </div>
        
        <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
          <!-- ✅ NUEVO: Monto Recibido para excedentes -->
          <div *ngIf="getFinancialSummary().hasExcessPayments" class="text-center">
            <p class="text-sm text-blue-700">Monto Recibido</p>
            <p class="font-bold text-purple-600">{{ getFinancialSummary().totalReceivedAmount | currency:'COP':'symbol':'1.0-0' }}</p>
            <p class="text-xs text-purple-500">Lo que pagó el cliente</p>
          </div>
          <div class="text-center">
            <p class="text-sm text-blue-700">Monto Bruto Total</p>
            <p class="font-bold text-blue-800">{{ getFinancialSummary().totalGrossAmount | currency:'COP':'symbol':'1.0-0' }}</p>
          </div>
          <div class="text-center">
            <p class="text-sm text-blue-700">Descuentos/Fees</p>
            <p class="font-bold text-red-600">-{{ getFinancialSummary().totalFees | currency:'COP':'symbol':'1.0-0' }}</p>
          </div>
          <div class="text-center">
            <p class="text-sm text-blue-700">Monto Neto Total</p>
            <p class="font-bold text-green-600">{{ getFinancialSummary().totalNetAmount | currency:'COP':'symbol':'1.0-0' }}</p>
          </div>
          <div class="text-center">
            <p class="text-sm text-blue-700">% Descuento</p>
            <p class="font-bold text-orange-600">{{ getFinancialSummary().discountPercentage | number:'1.2-2' }}%</p>
          </div>
        </div>
        
        <!-- ✅ NUEVO: Explicación del excedente -->
        <div *ngIf="isExcessRefund() && getExcessPaymentInfo()?.originReceipt" class="mt-3 pt-3 border-t border-blue-200">
          <p class="text-xs text-blue-700 mb-2"><strong>🔍 Explicación del Excedente:</strong></p>
          <div class="bg-white p-2 rounded border text-xs">
            <div class="flex justify-between items-center">
              <span>Monto Recibido:</span>
              <span class="font-medium">{{ getExcessPaymentInfo()?.originReceipt?.receivedAmount | currency:'COP':'symbol':'1.0-0' }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span>Monto Neto Aplicado:</span>
              <span class="font-medium">{{ getExcessPaymentInfo()?.originReceipt?.netAmount | currency:'COP':'symbol':'1.0-0' }}</span>
            </div>
            <div class="flex justify-between items-center border-t pt-1 mt-1 font-medium">
              <span>Excedente Generado:</span>
              <span class="text-yellow-700">{{ (getExcessPaymentInfo()?.originReceipt?.receivedAmount - getExcessPaymentInfo()?.originReceipt?.netAmount) | currency:'COP':'symbol':'1.0-0' }}</span>
            </div>
            <div class="flex justify-between items-center text-blue-600 font-medium">
              <span>Monto de Devolución:</span>
              <span>{{ getExcessPaymentInfo()?.refundableAmount | currency:'COP':'symbol':'1.0-0' }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Lista de RefundVoucherItems -->
      <div class="space-y-4">
        <div *ngFor="let item of getRefundVoucherItems(); let i = index" 
             class="border border-gray-200 rounded-lg p-4">
          
          <!-- Header del item -->
          <div class="flex justify-between items-start mb-3">
            <div>
              <h5 class="font-medium text-gray-800">
                <ng-container *ngIf="item.documentType === 'IncomeReceipt'; else noLink">
                  <a *ngIf="item.documentId" 
                     [routerLink]="['/income-receipts/view/', item.documentId]"
                     class="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                     title="Ver comprobante de ingreso">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                    {{ getDocumentTypeDescription(item.documentType) }} #{{ item.documentId }}
                  </a>
                </ng-container>
                <ng-template #noLink>
                  <span class="text-gray-800 text-sm flex items-center gap-1">
                    {{ getDocumentTypeDescription(item.documentType) }} 
                    <ng-container *ngIf="item.documentId">#{{ item.documentId }}</ng-container>
                  </span>
                </ng-template>
                
              </h5>
              <p class="text-sm text-gray-500">{{ item.documentReference }}</p>
              <!-- Información de la cuota afectada (si está en las observaciones del item) -->
              <p *ngIf="item.observations && item.observations.includes('Cuota afectada')" 
                 class="text-xs text-green-600 mt-1 bg-green-50 px-2 py-1 rounded inline-block">
                <i class="pi pi-calendar mr-1"></i>
                {{ item.observations }}
              </p>
            </div>
            <div class="text-right">
              <p class="text-sm text-gray-500">Monto Afectado</p>
              <p class="font-bold text-blue-600">{{ item.refundableAmount | currency:'COP':'symbol':'1.0-0' }}</p>
            </div>
          </div>
          
          <!-- Información básica -->
          <div class="grid grid-cols-2 gap-4 mb-3">
            <div>
              <p class="text-sm text-gray-500">Estado del Item</p>
              <span class="text-sm font-medium"
                    [class.text-green-600]="item.isFullyRefunded"
                    [class.text-yellow-600]="item.isPartiallyRefunded"
                    [class.text-gray-600]="!item.isPartiallyRefunded && !item.isFullyRefunded">
                {{ item.isFullyRefunded ? 'Completamente Devuelto' : 
                   item.isPartiallyRefunded ? 'Parcialmente Devuelto' : 'Pendiente' }}
              </span>
            </div>
            <div>
              <p class="text-sm text-gray-500">Monto Procesado</p>
              <p class="text-sm font-medium">{{ item.refundedAmount | currency:'COP':'symbol':'1.0-0' }}</p>
            </div>
          </div>
          
          <!-- Detalles expandibles -->
          <div *ngIf="showItemDetails && (item.paymentPlanDetail || item.excessPaymentDetail || item.incomeReceipts?.length > 0)" 
               class="border-t border-gray-100 pt-4 mt-4">
            
            <!-- Información del Plan (si aplica) -->
            <div *ngIf="item.paymentPlanDetail" class="mb-4">
              <h6 class="text-sm font-medium text-gray-700 mb-2">📋 Información del Plan</h6>
              <div class="bg-gray-50 rounded p-3">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span class="text-gray-600">Descripción:</span>
                    <span class="ml-1 font-medium">{{ item.paymentPlanDetail.description }}</span>
                  </div>
                  <div>
                    <span class="text-gray-600">Monto Bruto:</span>
                    <span class="ml-1 font-medium">{{ item.paymentPlanDetail.totalGrossAmount | currency:'COP':'symbol':'1.0-0' }}</span>
                  </div>
                  <div>
                    <span class="text-gray-600">Proporción:</span>
                    <span class="ml-1 font-medium">{{ (item.paymentPlanDetail.refundProportion * 100) | number:'1.2-2' }}%</span>
                  </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm mt-3">
                  <div>
                    <span class="text-gray-600">Cuota #:</span>
                    <span class="ml-1 font-medium">{{ item.paymentPlanDetail.installmentNumber }}</span>
                  </div>
                  <div>
                    <span class="text-gray-600">Fecha Vencimiento:</span>
                    <span class="ml-1 font-medium">{{ item.paymentPlanDetail.dueDate | date:'shortDate' }}</span>
                  </div>
                  <div>
                    <span class="text-gray-600">Monto a Pagar:</span>
                    <span class="ml-1 font-medium">{{ item.paymentPlanDetail.amountToPay | currency:'COP':'symbol':'1.0-0' }}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Información del Excedente de Pago (si aplica) -->
            <div *ngIf="item.excessPaymentDetail" class="mb-4">
              <h6 class="text-sm font-medium text-gray-700 mb-2">💰 Información del Excedente de Pago</h6>
              <div class="bg-yellow-50 rounded p-3 border border-yellow-200">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span class="text-gray-600">ID Excedente:</span>
                    <span class="ml-1 font-medium">#{{ item.excessPaymentDetail.excessPaymentId }}</span>
                  </div>
                  <div>
                    <span class="text-gray-600">Monto Excedente:</span>
                    <span class="ml-1 font-medium">{{ item.excessPaymentDetail.excessAmount | currency:'COP':'symbol':'1.0-0' }}</span>
                  </div>
                  <div>
                    <span class="text-gray-600">Disponible:</span>
                    <span class="ml-1 font-medium">{{ item.excessPaymentDetail.availableAmount | currency:'COP':'symbol':'1.0-0' }}</span>
                  </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mt-3">
                  <div>
                    <span class="text-gray-600">Fecha del Pago:</span>
                    <span class="ml-1 font-medium">{{ item.excessPaymentDetail.paymentDate | date:'shortDate' }}</span>
                  </div>
                  <div>
                    <span class="text-gray-600">Referencia:</span>
                    <span class="ml-1 font-medium">{{ item.excessPaymentDetail.paymentReference }}</span>
                  </div>
                </div>
                <!-- Mostrar información del comprobante de ingreso origen si está disponible -->
                <div *ngIf="item.excessPaymentDetail.originIncomeReceipt" class="mt-3 pt-3 border-t border-yellow-300">
                  <p class="text-xs font-medium text-gray-700 mb-2">📄 Comprobante de Ingreso que generó el excedente:</p>
                  <div class="flex justify-between items-center">
                    <div>
                      <span class="text-sm font-medium">Comprobante #{{ item.excessPaymentDetail.originIncomeReceipt.incomeReceiptId }}</span>
                      <p class="text-xs text-gray-600">{{ item.excessPaymentDetail.originIncomeReceipt.concept }}</p>
                    </div>
                    <a [routerLink]="['/income-receipts/view', item.excessPaymentDetail.originIncomeReceipt.incomeReceiptId]"
                       class="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1 bg-white px-2 py-1 rounded border"
                       title="Ver comprobante de ingreso">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                      Ver Comprobante
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Comprobantes de Ingreso asociados -->
            <div *ngIf="item.incomeReceipts?.length > 0">
              <h6 class="text-sm font-medium text-gray-700 mb-2">🧾 Comprobantes de Ingreso Asociados</h6>
              
              <div *ngFor="let ir of item.incomeReceipts; let j = index" 
                   class="bg-white border border-gray-200 rounded p-3 mb-3">
                
                <!-- Header del comprobante -->
                <div class="flex justify-between items-start mb-3">
                  <div>
                    <p class="text-sm font-medium text-gray-800">
                      Comprobante #{{ ir.incomeReceiptId }}
                    </p>
                    <p class="text-xs text-gray-500">{{ ir.transactionDate | date:'short' }}</p>
                    <p class="text-xs text-gray-600">{{ ir.concept }}</p>
                  </div>
                  <div class="text-right text-xs">
                    <p class="text-gray-500">Ref: {{ ir.referenceNumber }}</p>
                    <a [routerLink]="['/income-receipts/view', ir.incomeReceiptId]"
                       class="text-blue-600 hover:text-blue-800 underline text-xs flex items-center gap-1 mt-1"
                       title="Ver comprobante de ingreso">
                      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                      Ver Detalle
                    </a>
                  </div>
                </div>
                
                <!-- Desglose financiero -->
                <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <div class="bg-green-50 border border-green-200 rounded p-2 text-center">
                    <p class="text-xs text-green-700 font-medium">Monto Bruto</p>
                    <p class="text-sm font-bold text-green-800">{{ ir.grossAmount | currency:'COP':'symbol':'1.0-0' }}</p>
                    <p class="text-xs text-green-600">Lo que pagó el cliente</p>
                  </div>
                  <div class="bg-red-50 border border-red-200 rounded p-2 text-center">
                    <p class="text-xs text-red-700 font-medium">Total Descuentos</p>
                    <p class="text-sm font-bold text-red-800">-{{ ir.totalFees | currency:'COP':'symbol':'1.0-0' }}</p>
                    <p class="text-xs text-red-600">Fees y descuentos</p>
                  </div>
                  <div class="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                    <p class="text-xs text-blue-700 font-medium">Monto Neto</p>
                    <p class="text-sm font-bold text-blue-800">{{ ir.netAmount | currency:'COP':'symbol':'1.0-0' }}</p>
                    <p class="text-xs text-blue-600">Monto aplicado</p>
                  </div>
                </div>
                
                <!-- Detalle de fees/descuentos -->
                <div *ngIf="ir.feeDetails?.length > 0" class="border-t border-gray-100 pt-3">
                  <p class="text-xs font-medium text-gray-700 mb-2">🧮 Detalle de Descuentos/Fees:</p>
                  <div class="space-y-1">
                    <div *ngFor="let fee of ir.feeDetails" 
                         class="flex justify-between items-center text-xs bg-gray-50 rounded px-2 py-1">
                      <span class="text-gray-600">{{ getFeeTypeDescription(fee.feeTypeId, fee.description) }}</span>
                      <div class="text-right">
                        <span class="font-medium text-red-600">-{{ fee.amount | currency:'COP':'symbol':'1.0-0' }}</span>
                        <span *ngIf="fee.porcentualAmount > 0" class="text-gray-500 ml-1">({{ fee.porcentualAmount }}%)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Observaciones adicionales (si no son JSON) -->
          <div *ngIf="!item.hasDetailedInfo && item.observations" class="mt-3 p-3 bg-gray-50 rounded">
            <p class="text-sm text-gray-600">
              <strong>Observaciones:</strong> {{ item.observations }}
            </p>
          </div>
        </div>
      </div>
      
      <!-- Indicador de relación con excedentes -->
      <div *ngIf="hasExcessPaymentRelationship()" 
           class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div class="flex items-center gap-2">
          <svg class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p class="text-sm font-medium text-yellow-800">
            📊 Esta devolución está relacionada con excedentes de pago
          </p>
        </div>
        <p class="text-xs text-yellow-700 mt-1">
          Los montos y descuentos mostrados reflejan la trazabilidad completa desde los comprobantes originales.
        </p>
      </div>
    </div>
    
    <!-- Timeline and workflow -->
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <h3 class="text-lg font-medium text-gray-800 mb-4">Historial de la Devolución</h3>
      
      <div class="mb-4">
        <p class="text-sm text-gray-500">Estado Actual</p>
        <p class="font-medium">{{ refundVoucher.workflowStage }}</p>
      </div>
      
      <div class="relative">
        <!-- Timeline -->
        <div class="absolute left-4 top-0 h-full w-0.5 bg-gray-200"></div>
        
        <!-- Timeline events -->
        <div *ngFor="let event of auditTrail" class="relative pl-10 pb-6">
          <div class="absolute left-0 top-1 w-8 h-8 rounded-full flex items-center justify-center"
               [ngClass]="{
                 'bg-blue-100 text-blue-600': event.action === 'Creado',
                 'bg-green-100 text-green-600': event.action === 'Aprobado',
                 'bg-purple-100 text-purple-600': event.action === 'Procesado',
                 'bg-red-100 text-red-600': event.action === 'Rechazado' || event.action === 'Cancelado',
                 'bg-gray-100 text-gray-600': event.action === 'Conciliado'
               }">
            <svg *ngIf="event.action === 'Creado'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
            </svg>
            <svg *ngIf="event.action === 'Aprobado'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <svg *ngIf="event.action === 'Procesado'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <svg *ngIf="event.action === 'Rechazado' || event.action === 'Cancelado'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
            <svg *ngIf="event.action === 'Conciliado'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
          </div>
          
          <div>
            <div class="flex justify-between">
              <h4 class="font-medium">{{ event.action }}</h4>
              <span class="text-sm text-gray-500">{{ event.date | date:'medium' }}</span>
            </div>
            <p class="text-sm">{{ event.user }}</p>
            <p class="text-sm text-gray-600 mt-1">{{ event.details }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Approval Modal -->
  <div *ngIf="showApprovalModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
      <h3 class="text-lg font-medium text-gray-800 mb-4">Aprobar o Rechazar Devolución</h3>
      
      <form [formGroup]="approvalForm" (ngSubmit)="submitApproval()">
        <div class="space-y-4">
          <div class="flex items-center gap-4">
            <label class="inline-flex items-center">
              <input type="radio" 
                     formControlName="isApproved" 
                     [value]="true"
                     (change)="onApprovalStatusChange()"
                     class="form-radio h-4 w-4 text-green-600">
              <span class="ml-2 text-gray-700">Aprobar</span>
            </label>
            <label class="inline-flex items-center">
              <input type="radio" 
                     formControlName="isApproved" 
                     [value]="false"
                     (change)="onApprovalStatusChange()"
                     class="form-radio h-4 w-4 text-red-600">
              <span class="ml-2 text-gray-700">Rechazar</span>
            </label>
          </div>
          
          <div *ngIf="approvalForm.get('isApproved')?.value">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Monto Aprobado
            </label>
            <input type="number" 
                   formControlName="approvedAmount"
                   class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                   [min]="0.01"
                   [max]="refundVoucher?.refundAmount">
          </div>
          
          <div *ngIf="!approvalForm.get('isApproved')?.value">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Razón de Rechazo <span class="text-red-500">*</span>
            </label>
            <textarea formControlName="rejectionReason"
                      rows="3"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Indique la razón del rechazo"></textarea>
            <div *ngIf="approvalForm.get('rejectionReason')?.invalid && approvalForm.get('rejectionReason')?.touched" 
                 class="text-red-500 text-sm mt-1">
              Razón de rechazo es requerida
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Revisado por <span class="text-red-500">*</span>
            </label>
            <input type="text" 
                   formControlName="reviewedBy"
                   class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                   placeholder="Nombre de quien revisa">
            <div *ngIf="approvalForm.get('reviewedBy')?.invalid && approvalForm.get('reviewedBy')?.touched" 
                 class="text-red-500 text-sm mt-1">
              Nombre de revisor es requerido
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Comentarios
            </label>
            <textarea formControlName="reviewComments"
                      rows="2"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Comentarios adicionales (opcional)"></textarea>
          </div>
        </div>
        
        <div class="flex justify-end gap-3 mt-6">
          <button type="button" 
                  (click)="closeModals()"
                  class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition">
            Cancelar
          </button>
          <button type="submit" 
                  [disabled]="approvalForm.invalid"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-blue-300 disabled:cursor-not-allowed">
            Confirmar
          </button>
        </div>
      </form>
    </div>
  </div>
  
  <!-- 🆕 Process Modal (resumen + edición de campos clave antes de finalizar) -->
  <div *ngIf="showProcessModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-xl shadow-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
      <h3 class="text-lg font-medium text-gray-800 mb-4">Procesar Devolución — Resumen</h3>

      <!-- 🆕 Resumen read-only del comprobante -->
      <div class="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div>
          <span class="text-gray-500">Documento:</span>
          <span class="font-medium ml-1">{{ refundVoucher?.documentNumber }}</span>
        </div>
        <div>
          <span class="text-gray-500">Concepto:</span>
          <span class="font-medium ml-1">{{ refundVoucher?.refundConceptDescription }}</span>
        </div>
        <div>
          <span class="text-gray-500">Participante:</span>
          <span class="font-medium ml-1">{{ refundVoucher?.participant?.fullName || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-500">Proyecto:</span>
          <span class="font-medium ml-1">{{ refundVoucher?.project?.projectName || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-500">Banco (origen):</span>
          <span class="font-medium ml-1">{{ refundVoucher?.bank?.bankName || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-500">Monto total:</span>
          <span class="font-semibold ml-1">{{ formatCurrencyWithCode(refundVoucher?.refundAmount || 0) }}</span>
        </div>
        <div>
          <span class="text-gray-500">Ya procesado:</span>
          <span class="font-medium ml-1">{{ formatCurrencyWithCode(refundVoucher?.processedAmount || 0) }}</span>
        </div>
        <div>
          <span class="text-gray-500">Pendiente:</span>
          <span class="font-semibold text-purple-700 ml-1">{{ formatCurrencyWithCode(refundVoucher?.pendingAmount || 0) }}</span>
        </div>
      </div>

      <!-- 🆕 Items si la devolución es maestro-detalle -->
      <div *ngIf="(refundVoucher?.refundVoucherItems?.length || 0) > 0"
           class="border border-gray-200 rounded-lg p-3 mb-4">
        <p class="text-sm font-medium text-gray-700 mb-2">Items del comprobante</p>
        <ul class="divide-y divide-gray-100 text-sm">
          <li *ngFor="let item of refundVoucher.refundVoucherItems" class="py-2 flex justify-between">
            <span class="text-gray-600">{{ item.documentTypeDescription || item.documentType }} #{{ item.documentId }}</span>
            <span class="font-medium">{{ formatCurrencyWithCode(item.refundableAmount || 0) }}</span>
          </li>
        </ul>
      </div>

      <form [formGroup]="processForm" (ngSubmit)="submitProcess()">
        <div class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Monto a Procesar <span class="text-red-500">*</span>
              </label>
              <input type="number"
                     formControlName="amountToProcess"
                     class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                     [min]="0.01"
                     [max]="refundVoucher?.pendingAmount">
              <div *ngIf="processForm.get('amountToProcess')?.invalid && processForm.get('amountToProcess')?.touched"
                   class="text-red-500 text-sm mt-1">
                <span *ngIf="processForm.get('amountToProcess')?.errors?.['required']">Monto es requerido</span>
                <span *ngIf="processForm.get('amountToProcess')?.errors?.['min']">Monto debe ser mayor a 0</span>
              </div>
            </div>

            <!-- 🆕 Fecha de pago (editable) -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Fecha de pago
              </label>
              <input type="date"
                     formControlName="paymentDate"
                     class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
          </div>

          <!-- 🆕 Banco (editable sin necesidad de anular el comprobante) -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Banco que paga la devolución <span class="text-red-500">*</span>
            </label>
            <select formControlName="bankId"
                    class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option [ngValue]="null" disabled>Seleccionar banco</option>
              <option *ngFor="let b of banks" [ngValue]="b.id">{{ b.bankName }}</option>
            </select>
            <div *ngIf="processForm.get('bankId')?.invalid && processForm.get('bankId')?.touched"
                 class="text-red-500 text-sm mt-1">
              El banco es requerido
            </div>
            <p class="text-xs text-gray-500 mt-1">
              Banco original del comprobante: <span class="font-medium">{{ refundVoucher?.bank?.bankName || '-' }}</span>
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Referencia Bancaria
            </label>
            <input type="text"
                   formControlName="bankTransactionReference"
                   class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                   placeholder="Referencia de la transacción bancaria">
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Procesado por <span class="text-red-500">*</span>
            </label>
            <input type="text"
                   formControlName="processedBy"
                   class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                   placeholder="Nombre de quien procesa">
            <div *ngIf="processForm.get('processedBy')?.invalid && processForm.get('processedBy')?.touched"
                 class="text-red-500 text-sm mt-1">
              Nombre de procesador es requerido
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Observaciones
            </label>
            <textarea formControlName="processingObservations"
                      rows="2"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Observaciones adicionales (opcional)"></textarea>
          </div>

          <div class="flex items-center">
            <input type="checkbox"
                   id="isFinalProcessing"
                   formControlName="isFinalProcessing"
                   class="w-4 h-4 text-blue-600 rounded focus:ring-blue-500">
            <label for="isFinalProcessing" class="ml-2 text-sm font-medium text-gray-700">
              Es el procesamiento final
            </label>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button type="button"
                  (click)="closeModals()"
                  class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition">
            Cancelar
          </button>
          <button type="submit"
                  [disabled]="processForm.invalid"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-blue-300 disabled:cursor-not-allowed">
            Confirmar y procesar
          </button>
        </div>
      </form>
    </div>
  </div>
  
  <!-- 🆕 Anular Modal (patrón IncomeReceipts: solo motivo, usuario desde claims) -->
  <div *ngIf="showCancelModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
      <h3 class="text-lg font-medium text-gray-800 mb-4">Anular Devolución</h3>

      <form [formGroup]="cancelForm" (ngSubmit)="submitCancel()">
        <div class="space-y-4">
          <div class="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
            Al anular, se revertirá el estado de los comprobantes de ingreso asociados (IsRefunded = false) y se liberará el excedente reservado (si aplica). Esta acción no se puede deshacer.
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Motivo de la anulación <span class="text-red-500">*</span>
            </label>
            <textarea formControlName="reason"
                      rows="3"
                      class="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Indique el motivo de la anulación"></textarea>
            <div *ngIf="cancelForm.get('reason')?.invalid && cancelForm.get('reason')?.touched"
                 class="text-red-500 text-sm mt-1">
              El motivo es requerido
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button type="button"
                  (click)="closeModals()"
                  class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition">
            Volver
          </button>
          <button type="submit"
                  [disabled]="cancelForm.invalid"
                  class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-red-300 disabled:cursor-not-allowed">
            Confirmar anulación
          </button>
        </div>
      </form>
    </div>
  </div>
</div>